import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../housestyle.css";
import "./Blogs.css";
import BLOGS from "../data/Blogs";
import api from "../API/Api";
import PageHero from "../components/PageHero";

const CATEGORIES = ["All", "Announcement", "Sneakpeak", "Update", "Event", "Patch Notes"];
const SNEAKPEAK_SUBS = [
  "Weapon",
  "Mob",
  "POI",
  "Mechanic",
  "Event",
  "Map / Build",
];
const TAG_MAP = {
  new: "tag-new",
  fix: "tag-fix",
  change: "tag-change",
  event: "tag-event",
};

const video = 'https://pub-332780ae20b540038a9ea772f1bf747c.r2.dev/background_vids/vid_3.mp4';

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function Placeholder() {
  return (
    <div className="blog-img-placeholder">
      <span>No image</span>
    </div>
  );
}

function BlogCard({ blog }) {
  const navigate = useNavigate();
  const [views, setViews] = useState(null);

  useEffect(() => {
    api('getviews', { blog_id: blog.id }).then(res => {
      if (res.isSuccess) setViews(res.data?.views ?? 0);
    });
  }, [blog.id]);

  return (
    <div
      className="blog-card card fade-up"
      onClick={() => navigate(`/blogs/${blog.id}`)}
    >
      <div className="blog-card-image">
        {blog.thumbnail ? (
          <img src={blog.thumbnail} alt={blog.title} />
        ) : (
          <Placeholder />
        )}
        <span className="blog-card-category">{blog.category}</span>
      </div>
      <div className="blog-card-body">
        <div className="blog-card-tags">
          {blog.tags.map((t) => (
            <span key={t} className={`tag ${TAG_MAP[t] ?? ""}`}>
              {t}
            </span>
          ))}
          {blog.subcategory && (
            <span className="tag tag-change">{blog.subcategory}</span>
          )}
        </div>
        <div className="blog-card-title">{blog.title}</div>
        <p className="blog-card-desc">{blog.summary}</p>
        <div className="blog-card-footer">
          <span className="blog-card-date">{formatDate(blog.date)}</span>
          <div className="blog-card-footer-right">
            {views !== null && <span className="blog-card-views">👁 {views}</span>}
            <span className="blog-card-cta">Read more →</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const UPDATE_TYPE_LABEL = { launch: "Launch", major: "Major Update", minor: "Minor Update" };
const UPDATE_TAG_MAP    = { new: "tag-new", change: "tag-change", fix: "tag-fix", event: "tag-event" };

function UpdateCard({ update: u }) {
  return (
    <div className={`blog-card card fade-up update-card-blog update-type-${u.type}`}>
      <div className="blog-card-body">
        <div className="blog-card-tags">
          <span className="blog-card-category">Patch Notes</span>
          <span className="update-version-tag">{u.version}</span>
          <span className={`tag update-type-tag-${u.type}`}>
            {UPDATE_TYPE_LABEL[u.type] ?? u.type}
          </span>
        </div>
        <div className="blog-card-title">{u.title}</div>
        <p className="blog-card-desc">{u.summary}</p>
        {u.highlights?.length > 0 && (
          <ul className="update-highlights-blog">
            {u.highlights.map((h, i) => (
              <li key={i} className="update-highlight-item-blog">
                {u.tags?.[i] && (
                  <span className={`update-tag-blog ${UPDATE_TAG_MAP[u.tags[i]] ?? ""}`}>
                    {u.tag_labels?.[i] ?? u.tags[i]}
                  </span>
                )}
                {h}
              </li>
            ))}
          </ul>
        )}
        <div className="blog-card-footer" style={{ marginTop: "1rem" }}>
          <span className="blog-card-date">{u.date}</span>
        </div>
      </div>
    </div>
  );
}

export default function Blogs() {
  const [active, setActive] = useState("All");
  const [activeSub, setActiveSub] = useState("All");
  const [dbUpdates, setDbUpdates] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api("getseasonupdates", { season: 1 }).then((res) => {
      if (res.isSuccess) setDbUpdates(res.data ?? []);
    });
  }, []);

  const sorted = [...BLOGS].sort((a, b) => new Date(b.date) - new Date(a.date));
  const latest = sorted[0];

  const filtered = sorted.filter((b) => {
    if (active !== "All" && b.category !== active) return false;
    if (
      active === "Sneakpeak" &&
      activeSub !== "All" &&
      b.subcategory !== activeSub
    )
      return false;
    return true;
  });

  function handleCategoryClick(c) {
    setActive(c);
    setActiveSub("All");
  }

  return (
    <>
      <PageHero video={video} height="420px" eyebrow="News & Updates" title="Update " em="Blogs" subtitle="Stay up to date with new blogs, sneakpeaks and patch notes." />

      <div className="container section">
        {latest && (
          <>
            <div className="section-label">
              <span className="sl-text">Latest</span>
            </div>
            <div
              className="blog-featured card fade-up"
              onClick={() => navigate(`/blogs/${latest.id}`)}
            >
              <div className="blog-featured-image">
                {latest.thumbnail ? (
                  <img src={latest.thumbnail} alt={latest.title} />
                ) : (
                  <Placeholder />
                )}
              </div>
              <div className="blog-featured-body">
                <div
                  className="blog-card-tags"
                  style={{ marginBottom: "0.6rem" }}
                >
                  <span className="blog-card-category">{latest.category}</span>
                  {latest.tags.map((t) => (
                    <span key={t} className={`tag ${TAG_MAP[t] ?? ""}`}>
                      {t}
                    </span>
                  ))}
                </div>
                <div className="blog-featured-title">{latest.title}</div>
                <p className="blog-card-desc">{latest.summary}</p>
                <div className="blog-card-footer" style={{ marginTop: "1rem" }}>
                  <span className="blog-card-date">
                    {formatDate(latest.date)}
                  </span>
                  <span className="blog-card-cta">Read more →</span>
                </div>
              </div>
            </div>
          </>
        )}

        <div className="section-label" style={{ marginTop: "2.5rem" }}>
          <span className="sl-text">All Blogs</span>
        </div>

        <div className="blog-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              className={`btn ${active === c ? "btn-teal" : "btn-secondary"} blog-filter-btn`}
              onClick={() => handleCategoryClick(c)}
            >
              {c}
            </button>
          ))}
        </div>

        {active === "Sneakpeak" && (
          <div className="blog-filters blog-filters-sub">
            {["All", ...SNEAKPEAK_SUBS].map((s) => (
              <button
                key={s}
                className={`btn ${activeSub === s ? "btn-primary" : "btn-secondary"} blog-filter-btn`}
                onClick={() => setActiveSub(s)}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <div className="grid-auto blog-grid">
          {active === "Patch Notes"
            ? dbUpdates.map((u) => <UpdateCard key={u.id} update={u} />)
            : filtered.map((b) => <BlogCard key={b.id} blog={b} />)
          }
        </div>
      </div>
    </>
  );
}
